import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ClickAwayListener, Typography, makeStyles } from '@material-ui/core';
import { useCallback, useMemo, useState } from 'react';
import ArrowTooltip from '../../../arrowTooltip';
import TextBoxWithTooltip from '../../../textBoxWithTooltip';
import Scrollbars from '../../../scrollbars';
import {
  ABSENCE_FIELD_NAMES,
  ACCRUAL_OPTION_KEYS,
} from '../../../../../constants/absence';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import ActionButton from '../../../actionButton';
import { formatDate } from '../../../../../utility/dateUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(6),
    padding: spacing(8),
    width: '100%',
  },
  wrapper: {
    height: 28,
  },
  titleWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    paddingBottom: spacing(6),
  },
  data: {
    gap: spacing(3),
    display: 'grid',
  },
  info: {
    maxWidth: 520,
    display: 'grid',
    gridTemplateColumns: '80px 1fr',
    columnGap: spacing(2),
    whiteSpace: 'nowrap',
  },
  tooltip: {
    maxWidth: 600,
    width: 600,
    height: 'fit-content',
    maxHeight: 500,
    overflowY: 'auto',
    padding: 0,
    margin: 0,
  },
  balanceViewport: {
    maxHeight: 'calc(500px - 230px)',
    maxWidth: 520,
  },
  icon: {
    width: 24,
    height: 24,
  },
  footer: {
    borderTop: `1px solid ${primary.bluish7}`,
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: spacing(6),
  },
}));

const PLACEHOLDER_DATE = 'dd MMM yyyy';

const {
  AVAILABLE_DAYS,
  NUMBER_OF_DAYS,
  SCHEDULED_DAYS,
  CARRY_OVER,
  DAYS,
  EXPIRATION_DATE,
  NAME,
  ADJUSTMENTS,
  USED_DAYS,
  ACCRUAL,
} = ABSENCE_FIELD_NAMES;

const AbsenceInfoButton = ({
  translations,
  customClassName = undefined,
  absence,
  getBreakdownData,
  position = 'top',
  ...rest
}) => {
  const classes = useStyles();
  const [isOpened, setIsOpened] = useState(false);
  const [balance, setBalance] = useState({});

  const isMonthlyAccrual = balance[ACCRUAL] === ACCRUAL_OPTION_KEYS.MONTHLY;

  const totalBaseDays = useMemo(() => {
    const extensionDays = (balance?.extensions || []).reduce(
      (sum, extension) => sum + extension[DAYS],
      0
    );

    return balance[NUMBER_OF_DAYS] + extensionDays;
  }, [balance]);

  const renderInfo = (numberOfDays, label, index) => {
    return numberOfDays ? (
      <div key={index} className={classes.info}>
        <Typography variant="subtitle1">
          {`${numberOfDays} ${
            Math.abs(numberOfDays) !== 1 ? translations.days : translations.day
          }`}
        </Typography>
        <TextBoxWithTooltip text={label} />
      </div>
    ) : null;
  };

  const renderExpDate = expDate => {
    return expDate ? formatDate(expDate, PLACEHOLDER_DATE) : '-';
  };

  const formatDays = (value, isNegative = false) => {
    const numericValue = Number(value);

    if (numericValue === 0) {
      return value;
    }

    return numericValue < 0 || isNegative
      ? `-${Math.abs(numericValue)}`
      : `+${numericValue}`;
  };

  const prepareAvailableDaysValue = useMemo(() => {
    const currentMonth = new Date().getMonth() + 1;

    return isMonthlyAccrual
      ? Math.round((totalBaseDays / 12) * currentMonth)
      : balance[NUMBER_OF_DAYS];
  }, [balance, isMonthlyAccrual, totalBaseDays]);

  const prepareAvailableDaysMessage = useMemo(() => {
    const daysLabel =
      Math.abs(totalBaseDays) !== 1 ? translations.days : translations.day;

    return isMonthlyAccrual
      ? `${translations.availableInThisCycleMonthly} ${totalBaseDays} ${daysLabel} ${translations.availableInThisCycleMonthlySuffix}`
      : translations.availableInThisCycleYearly;
  }, [translations, totalBaseDays, isMonthlyAccrual]);

  const renderContent = () => {
    const carryOverMessage =
      balance[CARRY_OVER]?.[DAYS] < 0
        ? translations.negativeCarriedOver
        : `${translations.carriedOver} ${renderExpDate(
            balance[CARRY_OVER]?.[EXPIRATION_DATE]
          )}`;
    return (
      <div className={classes.root}>
        <div className={classes.titleWrapper}>
          <TextBoxWithTooltip
            variant="h5"
            text={`${translations.infoTitle} ${absence[NAME]}`}
          />
        </div>
        <Scrollbars viewportClass={classes.balanceViewport}>
          <div className={classes.data}>
            {renderInfo(
              formatDays(balance[CARRY_OVER]?.[DAYS]),
              carryOverMessage,
              'carry_over'
            )}
            {renderInfo(
              prepareAvailableDaysValue,
              prepareAvailableDaysMessage,
              'available_days'
            )}
            {!isMonthlyAccrual &&
              balance?.extensions?.map((extension, index) => {
                return renderInfo(
                  formatDays(extension[DAYS]),
                  extension[NAME],
                  `extension_${index}`
                );
              })}
            {renderInfo(
              formatDays(balance[ADJUSTMENTS]),
              translations.adjustmentDays
            )}
            {renderInfo(
              formatDays(balance[USED_DAYS], true),
              translations.usedDays
            )}
          </div>
        </Scrollbars>
        <div className={classes.footer}>
          <div>
            <TextBoxWithTooltip
              text={`${absence[AVAILABLE_DAYS]} ${
                Math.abs(absence[AVAILABLE_DAYS]) !== 1
                  ? translations.days
                  : translations.day
              }`}
              variant="subtitle1"
            />
            <TextBoxWithTooltip
              text={translations.totalDaysLabel}
              variant="subtitle1"
            />
          </div>
          <div>
            <TextBoxWithTooltip
              text={`${balance[SCHEDULED_DAYS]} ${
                Math.abs(balance[SCHEDULED_DAYS]) !== 1
                  ? translations.days
                  : translations.day
              }`}
              variant="subtitle1"
            />
            <TextBoxWithTooltip
              text={translations.numberOfScheduledDays}
              variant="subtitle1"
            />
          </div>
        </div>
      </div>
    );
  };

  const handleCloseTooltip = () => {
    setIsOpened(false);
  };

  const handleOpenTooltip = async e => {
    if (!isOpened) {
      e.preventDefault();
      e.stopPropagation();

      const breakdownData = await getBreakdownData(absence?.id).then(
        ({ data }) => data
      );
      setIsOpened(true);
      setBalance(breakdownData?.results || breakdownData);
    } else {
      handleCloseTooltip();
    }
  };

  const onTooltipContentClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={handleCloseTooltip}
    >
      <div onClick={onTooltipContentClick}>
        <ArrowTooltip
          open={isOpened}
          isArrowHidden
          tooltipLabel={renderContent()}
          tooltipClassName={classes.tooltip}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          position={position}
          {...rest}
        >
          <div className={classNames(classes.wrapper, customClassName)}>
            <ActionButton
              iconButtonClass={classes.icon}
              type={ACTION_BUTTON_TYPES.INFO}
              onClickHandler={handleOpenTooltip}
            />
          </div>
        </ArrowTooltip>
      </div>
    </ClickAwayListener>
  );
};

AbsenceInfoButton.propTypes = {
  translations: PropTypes.object.isRequired,
  customClassName: PropTypes.string,
  absence: PropTypes.object.isRequired,
  getBreakdownData: PropTypes.func.isRequired,
  position: PropTypes.string,
};

export default AbsenceInfoButton;
